import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { baseStyles } from '../styles/shared.js';

// ---------------------------------------------------------------------------
// <sysml-command-palette> — Quick-open overlay (Cmd+Shift+P)
//
// Fuzzy search on a list of registered commands. Each command has an id,
// label, optional keyboard shortcut string, and optional category.
// Selecting a command dispatches `@command-execute` with { commandId }.
// ---------------------------------------------------------------------------

export interface PaletteCommand {
  id: string;
  label: string;
  shortcut?: string;
  category?: string;
}

@customElement('sysml-command-palette')
export class SysmlCommandPalette extends LitElement {
  static override styles = [
    baseStyles,
    css`
      :host {
        position: fixed;
        inset: 0;
        z-index: 9999;
        display: flex;
        justify-content: center;
        padding-top: 80px;
        pointer-events: none;
      }
      :host([open]) {
        pointer-events: auto;
      }

      .backdrop {
        position: absolute;
        inset: 0;
        background: color-mix(in srgb, var(--r5-surface) 60%, transparent);
      }

      .palette {
        position: relative;
        width: 520px;
        max-height: 400px;
        display: flex;
        flex-direction: column;
        background: var(--r5-surface);
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-lg);
        box-shadow: 0 8px 32px color-mix(in srgb, var(--r5-surface) 40%, black);
        overflow: hidden;
      }

      .search-row {
        display: flex;
        align-items: center;
        gap: var(--r5-space-2);
        padding: var(--r5-space-2) var(--r5-space-3);
        border-bottom: 1px solid var(--r5-border);
      }

      .search-input {
        flex: 1;
        border: none;
        outline: none;
        background: none;
        color: var(--r5-text);
        font-size: var(--r5-font-sm);
        font-family: inherit;
      }
      .search-input::placeholder {
        color: var(--r5-text-muted);
      }

      .results {
        flex: 1;
        overflow-y: auto;
        padding: var(--r5-space-1) 0;
      }

      .result-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--r5-space-1) var(--r5-space-3);
        cursor: pointer;
        color: var(--r5-text);
        font-size: var(--r5-font-sm);
        line-height: 1.6;
      }
      .result-item:hover,
      .result-item.highlighted {
        background: color-mix(in srgb, var(--r5-primary) 10%, transparent);
      }

      .result-label {
        display: flex;
        align-items: center;
        gap: var(--r5-space-2);
        min-width: 0;
      }

      .result-category {
        color: var(--r5-text-muted);
        font-size: var(--r5-font-xs);
        flex-shrink: 0;
      }

      .result-shortcut {
        color: var(--r5-text-muted);
        font-size: var(--r5-font-xs);
        font-family: var(--r5-font-mono, monospace);
        flex-shrink: 0;
        padding: 1px 6px;
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-sm);
        background: var(--r5-surface-muted);
      }

      .no-results {
        padding: var(--r5-space-3);
        text-align: center;
        color: var(--r5-text-muted);
        font-size: var(--r5-font-sm);
      }
    `,
  ];

  @property({ type: Array }) commands: PaletteCommand[] = [];
  @property({ type: Boolean, reflect: true }) open = false;

  @state() private _query = '';
  @state() private _highlightIndex = 0;

  @query('.search-input') private _inputEl!: HTMLInputElement;

  override updated(changed: Map<string, unknown>): void {
    if (changed.has('open') && this.open) {
      this._query = '';
      this._highlightIndex = 0;
      // Focus the input after the element renders
      requestAnimationFrame(() => this._inputEl?.focus());
    }
  }

  private get _filteredCommands(): PaletteCommand[] {
    if (!this._query) return this.commands;
    const q = this._query.toLowerCase();
    return this.commands.filter(
      (cmd) =>
        cmd.label.toLowerCase().includes(q) ||
        (cmd.category?.toLowerCase().includes(q) ?? false),
    );
  }

  override render() {
    if (!this.open) return nothing;

    const filtered = this._filteredCommands;

    return html`
      <div class="backdrop" @click=${this._close}></div>
      <div
        class="palette"
        role="dialog"
        aria-modal="true"
        aria-label="Command Palette"
        @keydown=${this._onKeyDown}
      >
        <div class="search-row">
          <ark-icon name="search" size="sm"></ark-icon>
          <input
            class="search-input"
            type="text"
            placeholder="Type a command..."
            role="combobox"
            aria-expanded="true"
            aria-controls="palette-results"
            aria-activedescendant=${filtered.length > 0 ? `palette-item-${this._highlightIndex}` : ''}
            autocomplete="off"
            .value=${this._query}
            @input=${this._onInput}
          />
        </div>
        <div class="results" id="palette-results" role="listbox" aria-label="Commands">
          ${filtered.length > 0
            ? filtered.map(
                (cmd, i) => html`
                  <div
                    id=${`palette-item-${i}`}
                    class="result-item ${i === this._highlightIndex ? 'highlighted' : ''}"
                    role="option"
                    aria-selected=${i === this._highlightIndex ? 'true' : 'false'}
                    @click=${() => this._execute(cmd.id)}
                    @mouseenter=${() => { this._highlightIndex = i; }}
                  >
                    <span class="result-label">
                      ${cmd.category
                        ? html`<span class="result-category">${cmd.category}:</span>`
                        : nothing}
                      ${cmd.label}
                    </span>
                    ${cmd.shortcut
                      ? html`<span class="result-shortcut">${cmd.shortcut}</span>`
                      : nothing}
                  </div>
                `,
              )
            : html`<div class="no-results">No matching commands</div>`}
        </div>
      </div>
    `;
  }

  private _onInput(e: InputEvent): void {
    this._query = (e.target as HTMLInputElement).value;
    this._highlightIndex = 0;
  }

  private _onKeyDown(e: KeyboardEvent): void {
    const filtered = this._filteredCommands;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this._highlightIndex = Math.min(this._highlightIndex + 1, filtered.length - 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this._highlightIndex = Math.max(this._highlightIndex - 1, 0);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[this._highlightIndex]) {
        this._execute(filtered[this._highlightIndex].id);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this._close();
    } else if (e.key === 'Tab') {
      // Focus trap — keep focus inside the palette
      e.preventDefault();
    }
  }

  private _execute(commandId: string): void {
    this.dispatchEvent(
      new CustomEvent('command-execute', {
        detail: { commandId },
        bubbles: true,
        composed: true,
      }),
    );
    this._close();
  }

  private _close(): void {
    this.dispatchEvent(
      new CustomEvent('palette-close', {
        bubbles: true,
        composed: true,
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sysml-command-palette': SysmlCommandPalette;
  }
}
